import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogBody,
} from './ui/Dialog';

interface CookiePreferencesModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (preferences: {
    essential: boolean;
    analytical: boolean;
    marketing: boolean;
  }) => void;
}

export const CookiePreferencesModal: React.FC<CookiePreferencesModalProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const [, setEssential] = useState(true);
  const [analytical, setAnalytical] = useState(false);
  const [marketing, setMarketing] = useState(false);

  // Load current preferences when modal opens
  useEffect(() => {
    if (open) {
      try {
        const storedEssential = localStorage.getItem('cookie_consent_essential');
        const storedAnalytical = localStorage.getItem('cookie_consent_analytical');
        const storedMarketing = localStorage.getItem('cookie_consent_marketing');

        // Default to false if not set (GDPR compliance - not pre-checked)
        setEssential(storedEssential !== 'false');
        setAnalytical(storedAnalytical === 'true');
        setMarketing(storedMarketing === 'true');
      } catch {
        setEssential(true);
        setAnalytical(false);
        setMarketing(false);
      }
    }
  }, [open]);

  const handleSave = () => {
    onSave({
      essential: true, // Essential is always true
      analytical,
      marketing,
    });
  };

  const handleAcceptAll = () => {
    onSave({
      essential: true,
      analytical: true,
      marketing: true,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent size="md" showCloseButton>
        <DialogHeader>
          <DialogTitle>{t('cookieConsent.preferencesTitle')}</DialogTitle>
          <DialogDescription>
            {t('cookieConsent.preferencesDescription')}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-6">
            {/* Essential Cookies - Always enabled */}
            <div className="flex items-start justify-between gap-4 p-4 bg-surface-container rounded-lg border border-border-subtle">
              <div className="flex-1">
                <h4 className="font-semibold text-text-primary">
                  {t('cookieConsent.essential')}
                </h4>
                <p className="text-sm text-text-secondary mt-1">
                  {t('cookieConsent.essentialDesc')}
                </p>
              </div>
              <div className="shrink-0">
                <button
                  type="button"
                  disabled
                  className="relative inline-flex h-6 w-11 items-center rounded-full bg-accent-primary cursor-not-allowed opacity-70"
                >
                  <span className="inline-block h-4 w-4 transform translate-x-6 rounded-full bg-white transition-transform" />
                </button>
                <input type="hidden" name="essential" value="true" />
              </div>
            </div>

            {/* Analytical Cookies */}
            <div className="flex items-start justify-between gap-4 p-4 bg-surface-container rounded-lg border border-border-subtle">
              <div className="flex-1">
                <h4 className="font-semibold text-text-primary">
                  {t('cookieConsent.analytical')}
                </h4>
                <p className="text-sm text-text-secondary mt-1">
                  {t('cookieConsent.analyticalDesc')}
                </p>
              </div>
              <div className="shrink-0">
                <button
                  type="button"
                  onClick={() => setAnalytical(!analytical)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-accent-primary focus:ring-offset-2 focus:ring-offset-bg-elevated ${
                    analytical ? 'bg-accent-primary' : 'bg-border-strong'
                  }`}
                  role="switch"
                  aria-checked={analytical}
                  aria-label={t('cookieConsent.analytical')}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      analytical ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Marketing Cookies */}
            <div className="flex items-start justify-between gap-4 p-4 bg-surface-container rounded-lg border border-border-subtle">
              <div className="flex-1">
                <h4 className="font-semibold text-text-primary">
                  {t('cookieConsent.marketing')}
                </h4>
                <p className="text-sm text-text-secondary mt-1">
                  {t('cookieConsent.marketingDesc')}
                </p>
              </div>
              <div className="shrink-0">
                <button
                  type="button"
                  onClick={() => setMarketing(!marketing)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-accent-primary focus:ring-offset-2 focus:ring-offset-bg-elevated ${
                    marketing ? 'bg-accent-primary' : 'bg-border-strong'
                  }`}
                  role="switch"
                  aria-checked={marketing}
                  aria-label={t('cookieConsent.marketing')}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      marketing ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <button
              type="button"
              onClick={handleSave}
              className="px-4 py-3 bg-surface-container text-text-primary font-medium rounded-lg border border-border-subtle hover:bg-surface-container-low transition-colors min-h-[44px] focus:outline-none focus:ring-2 focus:ring-accent-primary focus:ring-offset-2 focus:ring-offset-bg-elevated"
            >
              {t('cookieConsent.savePreferences')}
            </button>
            <button
              type="button"
              onClick={handleAcceptAll}
              className="px-6 py-3 bg-accent-primary text-on-primary font-semibold rounded-lg hover:bg-accent-primary/90 transition-colors min-h-[44px] focus:outline-none focus:ring-2 focus:ring-accent-primary focus:ring-offset-2 focus:ring-offset-bg-elevated"
            >
              {t('cookieConsent.acceptAll')}
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
